import React, { useEffect, useState, useCallback } from 'react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  PieChart, Pie, Cell, ResponsiveContainer, LineChart, Line,
} from 'recharts';
import { fetchConveyancingReports } from '../api';
import { todayInGaborone } from '../utils/dates';

/* ── Palette ──────────────────────────────────────────────────────────────── */
const COLORS = {
  blue:    '#2563eb', // matches App.css --accent-color (Tailwind blue-600)
  violet:  '#7c3aed',
  teal:    '#0d9488',
  emerald: '#059669',
  amber:   '#d97706',
  red:     '#dc2626',
  sky:     '#0284c7',
  slate:   '#64748b',
};
const PIE_PALETTE = [COLORS.red, COLORS.amber, COLORS.teal, COLORS.blue, COLORS.violet, COLORS.slate];

const MONTH_NAMES = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
const fmtMonth = (ym) => {
  if (!ym) return '';
  const [y, m] = ym.split('-');
  return `${MONTH_NAMES[parseInt(m, 10) - 1]} ${y}`;
};
const shortMonth = (ym) => {
  if (!ym) return '';
  const m = parseInt(ym.split('-')[1], 10);
  return MONTH_NAMES[m - 1] || ym;
};

/* ── Custom Tooltip ───────────────────────────────────────────────────────── */
function ChartTooltip({ active, payload, label, unit = '' }) {
  if (!active || !payload?.length) return null;
  return (
    <div style={{
      background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8,
      padding: '10px 14px', boxShadow: '0 4px 12px rgba(0,0,0,.08)',
      fontSize: 13,
    }}>
      <div style={{ fontWeight: 600, color: '#334155', marginBottom: 4 }}>{label}</div>
      {payload.map((p, i) => (
        <div key={i} style={{ color: p.color, display: 'flex', gap: 8, alignItems: 'center' }}>
          <span style={{ width: 8, height: 8, borderRadius: '50%', background: p.color, display: 'inline-block' }} />
          <span>{p.name}: <strong>{p.value}{unit}</strong></span>
        </div>
      ))}
    </div>
  );
}

/* ── Pie label renderer ───────────────────────────────────────────────────── */
const RADIAN = Math.PI / 180;
const renderPieLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent }) => {
  if (percent < 0.06) return null;
  const r = innerRadius + (outerRadius - innerRadius) * 0.55;
  const x = cx + r * Math.cos(-midAngle * RADIAN);
  const y = cy + r * Math.sin(-midAngle * RADIAN);
  return (
    <text x={x} y={y} fill="#fff" textAnchor="middle" dominantBaseline="central"
      fontSize={12} fontWeight={700}>
      {`${(percent * 100).toFixed(0)}%`}
    </text>
  );
};

/* ── KPI Card ─────────────────────────────────────────────────────────────── */
/* Styled to match the BillingManagement summary cards (bg-white p-6 rounded-lg shadow-md). */
function KpiCard({ icon, label, value, sub, accent = COLORS.blue }) {
  return (
    <div
      className="bg-white p-6 rounded-lg shadow-md flex flex-col justify-center h-full"
      style={{ borderLeft: `4px solid ${accent}` }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
        {icon && <i className={`bi ${icon}`} style={{ fontSize: 16, color: accent }} />}
        <span style={{ fontSize: 12, fontWeight: 500, color: '#64748b', textTransform: 'uppercase', letterSpacing: '.4px' }}>
          {label}
        </span>
      </div>
      <div style={{ fontSize: 28, fontWeight: 700, color: '#1e293b', lineHeight: 1.1 }}>
        {value ?? <span style={{ color: '#cbd5e1' }}>&mdash;</span>}
      </div>
      {sub && <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 4 }}>{sub}</div>}
    </div>
  );
}

/* ── Section Card wrapper ─────────────────────────────────────────────────── */
/* Same chrome as BillingManagement cards. */
function Section({ title, subtitle, children, style, className = '' }) {
  return (
    <div className={`bg-white rounded-lg shadow-md overflow-hidden h-full ${className}`} style={style}>
      <div style={{ padding: '16px 22px 12px', borderBottom: '1px solid #f1f5f9' }}>
        <h6 style={{ margin: 0, fontWeight: 600, fontSize: 14, color: '#1e293b' }}>{title}</h6>
        {subtitle && <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 2 }}>{subtitle}</div>}
      </div>
      <div style={{ padding: '16px 22px 20px' }}>
        {children}
      </div>
    </div>
  );
}

/* ── Table helpers ────────────────────────────────────────────────────────── */
const thStyle = {
  fontSize: 11, fontWeight: 600, color: '#64748b', textTransform: 'uppercase',
  letterSpacing: '.3px', padding: '8px 12px', borderBottom: '2px solid #e2e8f0',
  background: '#f8fafc', position: 'sticky', top: 0,
};
const tdStyle = { fontSize: 13, padding: '8px 12px', borderBottom: '1px solid #f1f5f9', color: '#334155' };
const tdBoldStyle = { ...tdStyle, fontWeight: 600 };

/* ══════════════════════════════════════════════════════════════════════════ */
/*  MAIN COMPONENT                                                          */
/* ══════════════════════════════════════════════════════════════════════════ */
export default function ConveyancingReports({ authAxios }) {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);

  // Filter state
  const today = todayInGaborone();
  const oneYearAgo = new Date(Date.now() - 365 * 86400000).toISOString().split('T')[0];
  const [dateFrom, setDateFrom] = useState(oneYearAgo);
  const [dateTo, setDateTo] = useState(today);
  const [showFilters, setShowFilters] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setData(await fetchConveyancingReports(authAxios, {
        date_from: dateFrom,
        date_to: dateTo,
      }));
    }
    catch { setError('Failed to load conveyancing reports.'); }
    finally { setLoading(false); }
  }, [authAxios, dateFrom, dateTo]);

  useEffect(() => { load(); }, [load]);

  /* ── Loading state ───────────────────────────────────────────────────── */
  if (loading) return (
    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
      <div className="spinner-border text-primary" role="status" style={{ width: 48, height: 48, borderWidth: 3 }} />
    </div>
  );
  if (error) return (
    <div style={{ maxWidth: 520, margin: '40px auto', textAlign: 'center' }}>
      <i className="bi bi-exclamation-triangle" style={{ fontSize: 40, color: COLORS.amber }} />
      <p style={{ marginTop: 12, color: '#475569' }}>{error}</p>
      <button
        onClick={load}
        className="px-4 py-2 text-sm border border-blue-600 text-blue-600 rounded-md hover:bg-blue-50"
      >Retry</button>
    </div>
  );

  /* ── Destructure API data ────────────────────────────────────────────── */
  const {
    monthly_throughput          = [],
    per_conveyancer_throughput  = [],
    cycle_time                  = {},
    first_time_pass             = {},
    examiner_notes              = {},
    pipeline_by_stage           = [],
  } = data || {};

  /* ── Derived chart data ──────────────────────────────────────────────── */
  const monthlyChart = monthly_throughput.slice(-6).map(r => ({
    name: shortMonth(r.month),
    fullMonth: fmtMonth(r.month),
    'Cases Opened': r.cases_opened,
  }));

  const monthlyLineData = monthly_throughput.map(r => ({
    name: shortMonth(r.month),
    fullMonth: fmtMonth(r.month),
    cases: r.cases_opened,
  }));

  const conveyancerData = per_conveyancer_throughput.map(r => ({
    name: (r.name || 'Unknown').length > 14
      ? (r.name || 'Unknown').slice(0, 13) + '…'
      : (r.name || 'Unknown'),
    fullName: r.name || 'Unknown',
    Cases: r.cases_opened,
  }));

  const pipelineData = pipeline_by_stage.map(r => ({
    name: r.stage.length > 20 ? r.stage.slice(0, 19) + '…' : r.stage,
    fullStage: r.stage,
    Matters: r.count,
  }));

  const notesPie = Object.entries(examiner_notes.open_by_status ?? {}).map(([k, v]) => ({
    name: k.charAt(0) + k.slice(1).toLowerCase().replace('_', ' '),
    value: v,
  }));
  if (examiner_notes.overdue_count > 0) {
    notesPie.push({ name: 'Overdue', value: examiner_notes.overdue_count });
  }

  const ftpRate = first_time_pass.rate_percent;
  const ftpAccent = ftpRate >= 80 ? COLORS.emerald : ftpRate >= 60 ? COLORS.amber : COLORS.red;

  /* ── Render ──────────────────────────────────────────────────────────── */
  return (
    <div style={{ background: '#f8fafc', minHeight: '100vh', padding: '24px 28px 48px' }}>
      {/* ── Header ──────────────────────────────────────────────────────── */}
      <div style={{ marginBottom: 28 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <h4 style={{ margin: 0, fontWeight: 700, fontSize: 22, color: '#0f172a' }}>
              Conveyancing Reports
            </h4>
            <p style={{ margin: '4px 0 0', fontSize: 13, color: '#94a3b8' }}>
              Practice performance metrics and pipeline analytics
            </p>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={() => setShowFilters(!showFilters)}              style={{
                background: showFilters ? '#eef2ff' : '#fff', border: '1px solid #e2e8f0', borderRadius: 8,
                padding: '6px 14px', fontSize: 13, color: showFilters ? COLORS.blue : '#475569',
                display: 'flex', alignItems: 'center', gap: 6,
              }}>
              <i className="bi bi-funnel" /> Filters
            </button>
            <button onClick={load}              style={{
                background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8,
                padding: '6px 14px', fontSize: 13, color: '#475569',
                display: 'flex', alignItems: 'center', gap: 6,
              }}>
              <i className="bi bi-arrow-clockwise" /> Refresh
            </button>
          </div>
        </div>

        {/* ── Filter Bar ────────────────────────────────────────────────── */}
        {showFilters && (
          <div style={{
            marginTop: 16, padding: '14px 20px', background: '#fff', borderRadius: 10,
            border: '1px solid #e2e8f0', display: 'flex', flexWrap: 'wrap', gap: 16, alignItems: 'flex-end',
          }}>
            <div>
              <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: '#64748b', marginBottom: 4 }}>From</label>
              <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)}
                style={{ border: '1px solid #cbd5e1', borderRadius: 6, padding: '5px 10px', fontSize: 13 }} />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: '#64748b', marginBottom: 4 }}>To</label>
              <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)}
                style={{ border: '1px solid #cbd5e1', borderRadius: 6, padding: '5px 10px', fontSize: 13 }} />
            </div>
            <button onClick={load}              style={{
                background: COLORS.blue, color: '#fff', borderRadius: 6,
                padding: '6px 16px', fontSize: 13, border: 'none',
              }}>
              Apply
            </button>
            <button onClick={() => { setDateFrom(oneYearAgo); setDateTo(today); }}
                           style={{
                background: '#f1f5f9', color: '#475569', borderRadius: 6,
                padding: '6px 14px', fontSize: 13, border: '1px solid #e2e8f0',
              }}>
              Reset
            </button>
            {data?.filters && (
              <span style={{ fontSize: 11, color: '#94a3b8', marginLeft: 'auto' }}>
                Showing: {data.filters.date_from} to {data.filters.date_to}
              </span>
            )}
          </div>
        )}
      </div>

      {/* ── KPI Row (horizontal: 4-across from md, 2-across on sm, 1 on mobile) ─ */}
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <KpiCard
          icon="bi-clock-history"
          label="Avg Days to Registration"
          value={cycle_time.avg_days_to_registration != null
            ? `${cycle_time.avg_days_to_registration} days` : null}
          sub={`From ${cycle_time.completed_matters_count ?? 0} completed matters`}
          accent={COLORS.sky}
        />
        <KpiCard
          icon="bi-check2-circle"
          label="First-Time Pass Rate"
          value={ftpRate != null ? `${ftpRate}%` : null}
          sub={`${first_time_pass.first_time_passed ?? 0} of ${first_time_pass.total_concluded_instruments ?? 0} instruments`}
          accent={ftpAccent}
        />
        <KpiCard
          icon="bi-journal-text"
          label="Open Examiner Notes"
          value={(examiner_notes.open_by_status?.OPEN ?? 0) + (examiner_notes.open_by_status?.IN_PROGRESS ?? 0)}
          sub={
            examiner_notes.overdue_count > 0
              ? <span style={{ color: COLORS.red, fontWeight: 600 }}>{examiner_notes.overdue_count} overdue</span>
              : 'None overdue'
          }
          accent={COLORS.amber}
        />
        <KpiCard
          icon="bi-flag"
          label="Instruments with Notes"
          value={first_time_pass.instruments_with_notes ?? 0}
          sub="Raised examiner queries"
          accent={COLORS.slate}
        />
      </div>

      {/* ── Row 1: Monthly Throughput (horizontal from md) ──────────────── */}
      <div className="grid grid-cols-1 md:grid-cols-12 gap-4 mb-6">
        <div className="md:col-span-7">
          <Section title="Monthly Throughput" subtitle="New conveyancing matters opened per month">
            {monthlyChart.length === 0
              ? <p style={{ color: '#94a3b8', fontSize: 13 }}>No throughput data available.</p>
              : (
                <ResponsiveContainer width="100%" height={260}>
                  <BarChart data={monthlyChart} margin={{ top: 8, right: 12, left: -8, bottom: 4 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                    <XAxis dataKey="name" tick={{ fontSize: 12, fill: '#64748b' }} axisLine={{ stroke: '#e2e8f0' }} />
                    <YAxis allowDecimals={false} tick={{ fontSize: 12, fill: '#64748b' }} axisLine={false} tickLine={false} />
                    <Tooltip content={<ChartTooltip />} />
                    <Bar dataKey="Cases Opened" fill={COLORS.blue} radius={[6, 6, 0, 0]} maxBarSize={48} />
                  </BarChart>
                </ResponsiveContainer>
              )
            }
            {monthly_throughput.length > 6 && (
              <details style={{ marginTop: 12 }}>
                <summary style={{ fontSize: 12, color: COLORS.blue, cursor: 'pointer', fontWeight: 500 }}>
                  View full 12-month history
                </summary>
                <div style={{ maxHeight: 180, overflowY: 'auto', marginTop: 8, borderRadius: 8, border: '1px solid #e2e8f0' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead><tr>
                      <th style={thStyle}>Month</th>
                      <th style={{ ...thStyle, textAlign: 'right' }}>Cases Opened</th>
                    </tr></thead>
                    <tbody>
                      {[...monthly_throughput].reverse().map(r => (
                        <tr key={r.month}>
                          <td style={tdStyle}>{fmtMonth(r.month)}</td>
                          <td style={{ ...tdBoldStyle, textAlign: 'right' }}>{r.cases_opened}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </details>
            )}
          </Section>
        </div>

        <div className="md:col-span-5">
          <Section title="Per-Conveyancer Throughput" subtitle="Cases opened by team member">
            {conveyancerData.length === 0
              ? <p style={{ color: '#94a3b8', fontSize: 13 }}>No data available.</p>
              : (
                <>
                  <ResponsiveContainer width="100%" height={Math.max(140, conveyancerData.length * 40 + 20)}>
                    <BarChart layout="vertical" data={conveyancerData}
                      margin={{ top: 4, right: 20, left: 4, bottom: 4 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" horizontal={false} />
                      <XAxis type="number" allowDecimals={false} tick={{ fontSize: 11, fill: '#64748b' }} axisLine={false} tickLine={false} />
                      <YAxis type="category" dataKey="name" width={80} tick={{ fontSize: 11, fill: '#475569' }} axisLine={false} tickLine={false} />
                      <Tooltip content={<ChartTooltip />} />
                      <Bar dataKey="Cases" fill={COLORS.teal} radius={[0, 6, 6, 0]} maxBarSize={28} />
                    </BarChart>
                  </ResponsiveContainer>
                  <div style={{ maxHeight: 140, overflowY: 'auto', marginTop: 8, borderRadius: 8, border: '1px solid #e2e8f0' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                      <thead><tr>
                        <th style={thStyle}>Conveyancer</th>
                        <th style={{ ...thStyle, textAlign: 'right' }}>Cases</th>
                      </tr></thead>
                      <tbody>
                        {per_conveyancer_throughput.map(r => (
                          <tr key={r.user_id}>
                            <td style={tdStyle}>{r.name || '(Unknown)'}</td>
                            <td style={{ ...tdBoldStyle, textAlign: 'right' }}>{r.cases_opened}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              )
            }
          </Section>
        </div>
      </div>

      {/* ── Row 2: Pipeline + Examiner Notes (horizontal from md) ──────── */}
      <div className="grid grid-cols-1 md:grid-cols-12 gap-4 mb-6">
        <div className="md:col-span-7">
          <Section title="Active Pipeline by Stage" subtitle="Current matters distributed across workflow stages">
            {pipelineData.length === 0
              ? <p style={{ color: '#94a3b8', fontSize: 13 }}>No active matters in pipeline.</p>
              : (
                <>
                  <ResponsiveContainer width="100%" height={Math.max(160, pipelineData.length * 38 + 20)}>
                    <BarChart layout="vertical" data={pipelineData}
                      margin={{ top: 4, right: 20, left: 4, bottom: 4 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" horizontal={false} />
                      <XAxis type="number" allowDecimals={false} tick={{ fontSize: 11, fill: '#64748b' }} axisLine={false} tickLine={false} />
                      <YAxis type="category" dataKey="name" width={140} tick={{ fontSize: 11, fill: '#475569' }} axisLine={false} tickLine={false} />
                      <Tooltip content={<ChartTooltip />} />
                      <Bar dataKey="Matters" fill={COLORS.violet} radius={[0, 6, 6, 0]} maxBarSize={26} />
                    </BarChart>
                  </ResponsiveContainer>
                  <div style={{ maxHeight: 180, overflowY: 'auto', marginTop: 8, borderRadius: 8, border: '1px solid #e2e8f0' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                      <thead><tr>
                        <th style={thStyle}>Stage</th>
                        <th style={{ ...thStyle, textAlign: 'right' }}>Matters</th>
                      </tr></thead>
                      <tbody>
                        {pipeline_by_stage.map(r => (
                          <tr key={r.stage}>
                            <td style={tdStyle}>{r.stage}</td>
                            <td style={{ ...tdBoldStyle, textAlign: 'right' }}>{r.count}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              )
            }
          </Section>
        </div>

        <div className="md:col-span-5">
          <Section title="Examiner Notes Breakdown" subtitle="Distribution of open examiner queries">
            {notesPie.length === 0
              ? (
                <div style={{ textAlign: 'center', padding: '32px 0' }}>
                  <i className="bi bi-check-circle" style={{ fontSize: 36, color: COLORS.emerald }} />
                  <p style={{ color: '#64748b', fontSize: 13, marginTop: 8 }}>
                    No open examiner notes &mdash; all clear.
                  </p>
                </div>
              ) : (
                <>
                  <ResponsiveContainer width="100%" height={220}>
                    <PieChart>
                      <Pie
                        data={notesPie} cx="50%" cy="50%"
                        outerRadius={90} innerRadius={40}
                        dataKey="value" labelLine={false}
                        label={renderPieLabel}
                        stroke="#fff" strokeWidth={2}
                      >
                        {notesPie.map((entry, i) => (
                          <Cell key={entry.name} fill={PIE_PALETTE[i % PIE_PALETTE.length]} />
                        ))}
                      </Pie>
                      <Tooltip formatter={(val, name) => [val, name]} />
                      <Legend
                        iconType="circle" iconSize={8}
                        formatter={(val) => <span style={{ fontSize: 12, color: '#475569' }}>{val}</span>}
                      />
                    </PieChart>
                  </ResponsiveContainer>

                  <div style={{ marginTop: 12, borderRadius: 8, overflow: 'hidden', border: '1px solid #e2e8f0' }}>
                    {Object.entries(examiner_notes.open_by_status ?? {}).map(([st, count]) => (
                      <div key={st} style={{
                        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                        padding: '8px 14px', borderBottom: '1px solid #f1f5f9',
                      }}>
                        <span style={{
                          fontSize: 11, fontWeight: 600, padding: '2px 10px', borderRadius: 12,
                          background: st === 'OPEN' ? '#fef2f2' : '#fffbeb',
                          color: st === 'OPEN' ? COLORS.red : COLORS.amber,
                        }}>
                          {st.replace('_', ' ')}
                        </span>
                        <span style={{ fontWeight: 600, fontSize: 14, color: '#1e293b' }}>{count}</span>
                      </div>
                    ))}
                    {examiner_notes.overdue_count > 0 && (
                      <div style={{
                        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                        padding: '8px 14px', background: '#fef2f2',
                      }}>
                        <span style={{ fontSize: 12, color: COLORS.red, fontWeight: 600 }}>
                          <i className="bi bi-exclamation-triangle-fill me-1" /> Overdue
                        </span>
                        <span style={{ fontWeight: 700, fontSize: 14, color: COLORS.red }}>
                          {examiner_notes.overdue_count}
                        </span>
                      </div>
                    )}
                  </div>
                </>
              )
            }
          </Section>
        </div>
      </div>

      {/* ── Row 3: Trend Line (if enough data) ─────────────────────────── */}
      {monthlyLineData.length >= 3 && (
        <div className="grid grid-cols-1 gap-4">
          <div>
            <Section title="12-Month Trend" subtitle="Monthly case volume over the past year">
              <ResponsiveContainer width="100%" height={200}>
                <LineChart data={monthlyLineData} margin={{ top: 8, right: 20, left: -8, bottom: 4 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                  <XAxis dataKey="name" tick={{ fontSize: 11, fill: '#64748b' }} axisLine={{ stroke: '#e2e8f0' }} />
                  <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: '#64748b' }} axisLine={false} tickLine={false} />
                  <Tooltip content={<ChartTooltip />} />
                  <Line
                    type="monotone" dataKey="cases" name="Cases Opened"
                    stroke={COLORS.blue} strokeWidth={2.5}
                    dot={{ r: 4, fill: COLORS.blue, strokeWidth: 2, stroke: '#fff' }}
                    activeDot={{ r: 6, fill: COLORS.blue, strokeWidth: 2, stroke: '#fff' }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </Section>
          </div>
        </div>
      )}
    </div>
  );
}
